import React, { FC, useState, useEffect } from 'react';
import { Box, Text, Flex } from 'rebass/styled-components';
import styled from 'styled-components';
import Upload from '../Upload';
import firebase from 'firebase';
import { LoadingBar } from '../loadingBar';
import uniqid from 'uniqid';
import useGallery from 'hooks/useGallery';
import GalleryDropzone from './galleryDropzone';
import imageCompression from 'browser-image-compression';
import { checkBeforeUpload } from 'util/duplicates';

// UI refresh (5 Nov 2026): rounded image cards with a 16:9 frame and an inline name field.
const ImageInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0 8px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: #2e333b;
  text-align: center;
  :hover,
  :focus {
    border-color: #b9bec6;
    background: #f9fafb;
    outline: none;
  }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  padding: 6px 6px 20px;
`;

const Thumbnail = styled.div`
  border: 1px solid #e4e6ea;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
  background: #16181d;
`;

const GalleryContainer: FC<{
  projectID: string;
  files: IAsset[];
  isImagesLoading: boolean;
  isUploading(state: boolean): void;
}> = ({ projectID, files, isImagesLoading }) => {
  const [uploading, setUploading] = useState(false);
  const { uploadImages, updateImages } = useGallery();
  const [uploads, _setUploads] = useState<{ task: firebase.storage.UploadTask; id: string; fileName: string }[]>([]);
  const uploadsRef = React.useRef<{ task: firebase.storage.UploadTask; id: string; fileName: string }[]>(uploads);
  const setUploads = (uploads: { task: firebase.storage.UploadTask; id: string; fileName: string }[]) => {
    uploadsRef.current = uploads;
    _setUploads(uploads);
  };

  const [imageIds, setImageIds] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const initialImageIds = files.reduce((acc, image) => {
      acc[image.imageId!] = image.id;
      return acc;
    }, {} as { [key: string]: string });

    setImageIds(initialImageIds);
  }, [files, uploading]);

  const uploadMultipleFiles = async (dropped: File) => {
    const file = await checkBeforeUpload('gallery', dropped, projectID);
    if (!file) return;
    setUploading(true);
    const options = {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      uploadImages(compressedFile, projectID, (task) => {
        setUploads([...uploadsRef.current, { task: task, id: uniqid(), fileName: compressedFile.name }]);
      }).then((res) => {
        if (res) {
          setImageIds((prevState) => ({
            ...prevState,
            [res.toString()!]: compressedFile.name,
          }));
        }
      });
    } catch (error) {
      console.error('Error compressing file:', error);
    }
  };

  const handleInputChange = (id: string, value: string) => {
    setImageIds((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const updateImageName = (imageId: string, updatedName: string) => {
    updateImages(imageId, { id: updatedName });
  };

  return (
    <>
      <Box bg={'white'} width={1} height={'100%'}>
        <LoadingBar loading={uploading || isImagesLoading} />
        <Flex height={'100%'} flexDirection="column" style={{ flexGrow: 1 }}>
          {uploads.map((upload, index) => (
            <Upload key={`video${index}`} upload={upload.task} fileName={upload.fileName} uploading={setUploading} />
          ))}
          <GalleryDropzone upload={uploadMultipleFiles}>
            <Gallery>
              {files.map((image, index) => (
                <React.Fragment key={`${image.id}+${index}`}>
                  <Thumbnail>
                    {image?.downloadURL && <ThumbnailImage src={image.downloadURL} alt={image.id} />}
                    <div style={{ padding: 4 }}>
                      <ImageInput
                        aria-label="Image name"
                        value={imageIds[image.imageId!] || ''}
                        type="text"
                        placeholder="Name this image"
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => updateImageName(image.imageId!, e.target.value)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(image.imageId!, e.target.value)}
                      />
                    </div>
                  </Thumbnail>
                </React.Fragment>
              ))}
            </Gallery>
          </GalleryDropzone>
        </Flex>
      </Box>
    </>
  );
};

export default GalleryContainer;
